import { useEffect, useState, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../utils/api'
import { Asset, Vendor } from '../types'
import { ClipboardList, AlertTriangle, Calendar } from 'lucide-react'

const empty = {
  assetCode:'',assetTag:'',name:'',type:'LAPTOP',category:'',serialNumber:'',brand:'',model:'',spec:'',status:'AVAILABLE',
  companyId:'',siteId:'',departmentId:'',building:'',floor:'',room:'',
  employeeId:'',assignedTo:'',userId:'',
  deliveryDocMA:'',maDate:'',nextMaDate:'',
  vendorId:'',serviceContract:'',
  ipAddress:'',macEthernet:'',macWifi:'',macBluetooth:'',
  purchaseDate:'',warrantyExpiry:'',contractEnd:'',
  rentPrice:'',purchasePrice:'',netBook:'',depreciation:'',currentValue:'',costCenter:'',
  licenseInfo:'',notes:'',
  cpu:'',vga:'',ram:'',hdd:'',monitorSpec:''
}

function Sec({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 space-y-4">
      <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wide pb-2 border-b border-gray-100">{title}</h2>
      {children}
    </div>
  )
}
function Grid({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>
}
function Field({ label, req, children }: { label: string; req?: boolean; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}{req && <span className="text-red-500 ml-0.5">*</span>}</label>
      {children}
    </div>
  )
}
const ic = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"

function DateInput({ value, onChange, className }: { value: string; onChange: (v: string) => void; className?: string }) {
  const [display, setDisplay] = useState('')
  const dateRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (value) {
      const [y, m, d] = value.split('-')
      if (y && m && d) setDisplay(`${d}/${m}/${y}`)
    } else {
      setDisplay('')
    }
  }, [value])

  const handleText = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, '').slice(0, 8)
    let formatted = raw
    if (raw.length > 4) formatted = `${raw.slice(0,2)}/${raw.slice(2,4)}/${raw.slice(4)}`
    else if (raw.length > 2) formatted = `${raw.slice(0,2)}/${raw.slice(2)}`
    setDisplay(formatted)
    if (raw.length === 8) {
      const iso = `${raw.slice(4)}-${raw.slice(2,4)}-${raw.slice(0,2)}`
      if (!isNaN(new Date(iso).getTime())) onChange(iso)
    } else if (raw.length === 0) {
      onChange('')
    }
  }

  return (
    <div className="relative">
      <input type="text" value={display} onChange={handleText} placeholder="dd/mm/yyyy" maxLength={10}
        className={className} style={{ paddingRight: '2rem' }} />
      <input ref={dateRef} type="date" value={value} onChange={e => onChange(e.target.value)}
        className="sr-only" tabIndex={-1} />
      <button type="button" onClick={() => (dateRef.current as any)?.showPicker?.()}
        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
        <Calendar size={15}/>
      </button>
    </div>
  )
}

export default function AssetFormPage() {
  const { id } = useParams<{id:string}>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const isEdit = !!id
  const [form, setForm] = useState(empty)
  const [error, setError] = useState('')
  const originalAssignedTo = useRef('')
  const [deliveryPrompt, setDeliveryPrompt] = useState<{assetId:string;newUser:string;oldUser:string;employeeId:string}|null>(null)

  const { data: asset } = useQuery<Asset>({
    queryKey: ['asset', id], enabled: isEdit,
    queryFn: () => api.get(`/assets/${id}`).then(r => r.data),
  })
  const { data: rooms = [] }       = useQuery<{id:string;name:string}[]>({ queryKey: ['masterdata','rooms'], queryFn: () => api.get('/masterdata/rooms').then(r=>r.data) })
  const { data: brands = [] }      = useQuery<{id:string;name:string}[]>({ queryKey: ['masterdata','brands'], queryFn: () => api.get('/masterdata/brands').then(r=>r.data) })
  const { data: assetTypes = [] }  = useQuery<{id:string;label:string;value:string}[]>({ queryKey: ['masterdata','asset-types'], queryFn: () => api.get('/masterdata/asset-types').then(r=>r.data) })
  const { data: assetStatuses = [] } = useQuery<{id:string;label:string;value:string}[]>({ queryKey: ['masterdata','asset-statuses'], queryFn: () => api.get('/masterdata/asset-statuses').then(r=>r.data) })
  const { data: assetCategories = [] } = useQuery<{id:string;name:string}[]>({ queryKey: ['masterdata','asset-categories'], queryFn: () => api.get('/masterdata/asset-categories').then(r=>r.data) })
  const { data: vendors = [] }     = useQuery<Vendor[]>({ queryKey: ['vendors'], queryFn: () => api.get('/vendors').then(r=>r.data) })
  const { data: companies = [] }   = useQuery<{id:string;name:string}[]>({ queryKey: ['masterdata','companies'], queryFn: () => api.get('/masterdata/companies').then(r=>r.data).catch(()=>[]) })
  const { data: allSites = [] }    = useQuery<{id:string;name:string;companyId?:string;company?:{id:string}}[]>({ queryKey: ['masterdata','sites'], queryFn: () => api.get('/masterdata/sites').then(r=>r.data).catch(()=>[]) })
  const { data: allDepts = [] }    = useQuery<{id:string;name:string;siteId?:string;site?:{id:string}}[]>({ queryKey: ['masterdata','departments'], queryFn: () => api.get('/masterdata/departments').then(r=>r.data).catch(()=>[]) })
  const { data: allRooms = [] }    = useQuery<{id:string;name:string;siteId?:string;site?:{id:string}}[]>({ queryKey: ['masterdata','rooms'], queryFn: () => api.get('/masterdata/rooms').then(r=>r.data).catch(()=>[]) })
  const { data: allUsers = [] }    = useQuery<{id:string;username:string;displayName?:string;email?:string;employeeId?:string;companyId?:string;isActive:boolean}[]>({ queryKey: ['users'], queryFn: () => api.get('/users').then(r=>r.data?.users || []).catch(()=>[]) })
  const [userSearch, setUserSearch] = useState('')

  useEffect(() => {
    if (!asset) return
    originalAssignedTo.current = asset.assignedTo || ''
    const d = (v?: string) => v ? v.split('T')[0] : ''
    setForm({
      assetCode: asset.assetCode||'', assetTag: asset.assetTag||'',
      name: asset.name,
      type: asset.type, category: asset.category||'', serialNumber: asset.serialNumber||'',
      brand: asset.brand||'', model: asset.model||'', spec: asset.spec||'', status: asset.status,
      building: asset.building||'', floor: asset.floor||'', room: asset.room||'',
      companyId: (asset as any).companyId||'', siteId: (asset as any).siteId||'', departmentId: (asset as any).departmentId||'',
      employeeId: asset.employeeId||'', assignedTo: asset.assignedTo||'', userId: (asset as any).userId||'',
      deliveryDocMA: asset.deliveryDocMA||'',
      maDate: d(asset.maDate), nextMaDate: d(asset.nextMaDate),
      vendorId: asset.vendorId||'', serviceContract: asset.serviceContract||'',
      ipAddress: asset.ipAddress||'', macEthernet: asset.macEthernet||'',
      macWifi: asset.macWifi||'', macBluetooth: asset.macBluetooth||'',
      purchaseDate: d(asset.purchaseDate), warrantyExpiry: d(asset.warrantyExpiry), contractEnd: d(asset.contractEnd),
      rentPrice: asset.rentPrice?.toString()||'', purchasePrice: asset.purchasePrice?.toString()||'',
      netBook: asset.netBook?.toString()||'', depreciation: asset.depreciation?.toString()||'',
      currentValue: asset.currentValue?.toString()||'', costCenter: asset.costCenter||'',
      licenseInfo: asset.licenseInfo||'', notes: asset.notes||'',
      cpu: asset.cpu||'', vga: asset.vga||'',
      ram: asset.ram||'', hdd: asset.hdd||'',
      monitorSpec: asset.monitorSpec||'',
    })
  }, [asset])

  const mutation = useMutation({
    mutationFn: (data: any) => isEdit ? api.put(`/assets/${id}`, data) : api.post('/assets', data),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['assets'] });
      qc.invalidateQueries({ queryKey: ['asset', res.data.id] });
      const savedId = res.data.id
      const newUser = form.assignedTo
      const oldUser = originalAssignedTo.current
      if (newUser && newUser !== oldUser) {
        setDeliveryPrompt({ assetId: savedId, newUser, oldUser, employeeId: form.employeeId })
      } else {
        navigate(`/assets/${savedId}`)
      }
    },
    onError: (e: any) => {
      const msg = e.response?.data?.message || 'เกิดข้อผิดพลาด'
      setError(msg)
      alert(msg)
    },
  })

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.assetCode.trim()) { setError('กรุณากรอก Asset Code'); alert('กรุณากรอก Asset Code'); return }
    const n = (v: string) => v ? parseFloat(v) : null
    const dt = (v: string) => v || null
    // name = assetCode (ใช้อันเดียวกัน)
    mutation.mutate({
      ...form,
      name: form.assetCode,   // name = assetCode
      purchaseDate: dt(form.purchaseDate), warrantyExpiry: dt(form.warrantyExpiry),
      contractEnd: dt(form.contractEnd), maDate: dt(form.maDate), nextMaDate: dt(form.nextMaDate),
      rentPrice: n(form.rentPrice), purchasePrice: n(form.purchasePrice),
      netBook: n(form.netBook), depreciation: n(form.depreciation), currentValue: n(form.currentValue),
      serialNumber: form.serialNumber||null, vendorId: form.vendorId||null, userId: form.userId||null,
    })
  }

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement>) =>
    setForm(f => ({...f, [k]: e.target.value}))

  // cascade filters
  const filteredSites = form.companyId ? allSites.filter((s:any)=>s.companyId===form.companyId||s.company?.id===form.companyId) : allSites
  const filteredDepts = form.siteId ? allDepts.filter((d:any)=>d.siteId===form.siteId||d.site?.id===form.siteId) : allDepts
  const filteredRooms = form.siteId ? allRooms.filter((r:any)=>r.siteId===form.siteId||r.site?.id===form.siteId) : allRooms
  const companyUsers  = (form.companyId ? allUsers.filter(u=>u.companyId===form.companyId) : allUsers).filter(u=>u.isActive)
  const filteredUsers = userSearch
    ? companyUsers.filter(u =>
        (u.displayName||u.username).toLowerCase().includes(userSearch.toLowerCase()) ||
        u.email?.toLowerCase().includes(userSearch.toLowerCase()) ||
        u.employeeId?.toLowerCase().includes(userSearch.toLowerCase()))
    : companyUsers

  const handleSelectUser = (selectedUserId: string) => {
    const u = allUsers.find(x => x.id === selectedUserId)
    if (!u) { setForm(f => ({ ...f, assignedTo: '', employeeId: '', userId: '' })); return }
    setForm(f => ({
      ...f,
      assignedTo: u.displayName || u.username,
      employeeId: u.employeeId || '',
      userId: u.id,
    }))
    setUserSearch('')
  }
  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex items-center justify-between px-6 py-4 bg-white border-b border-gray-100 flex-shrink-0">
        <h1 className="text-xl font-bold text-gray-900">{isEdit ? 'แก้ไข Asset' : 'เพิ่ม Asset ใหม่'}</h1>
        <div className="flex gap-3">
          <button type="button" onClick={() => navigate(-1)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">ยกเลิก</button>
          <button type="submit" form="asset-form" disabled={mutation.isPending} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
            {mutation.isPending ? 'กำลังบันทึก...' : isEdit ? 'บันทึกการแก้ไข' : 'เพิ่ม Asset'}
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
      <div className="p-6 max-w-3xl mx-auto pb-16">
      <form id="asset-form" onSubmit={submit} className="space-y-5">

        {/* พื้นฐาน */}
        <Sec title="ข้อมูลพื้นฐาน">
          <Grid>
            {/* Asset Code = ชื่อหลัก */}
            <Field label="Asset Code" req>
              <input value={form.assetCode} onChange={set('assetCode')} required className={ic} placeholder="เช่น N0286"/>
              <p className="text-xs text-gray-400 mt-1">รหัสประจำเครื่อง — ใช้เป็นชื่อหลักของ Asset</p>
            </Field>
            <Field label="รหัสทรัพย์สิน (Asset Tag)"><input value={form.assetTag} onChange={set('assetTag')} className={ic} placeholder="เช่น CZ03-21000016"/></Field>
            <Field label="Serial Number"><input value={form.serialNumber} onChange={set('serialNumber')} className={ic} placeholder="S/N..."/></Field>
            <Field label="ประเภท">
              <select value={form.type} onChange={set('type')} className={ic}>
                {assetTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </Field>
            <Field label="หมวดหมู่ (Category)">
              <select value={form.category} onChange={set('category')} className={ic}>
                <option value="">-- เลือก Category --</option>
                {assetCategories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
            </Field>
            <Field label="สถานะ">
              <select value={form.status} onChange={set('status')} className={ic}>
                {assetStatuses.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </Field>
            <Field label="ยี่ห้อ (Brand)">
              {brands.length > 0
                ? <select value={form.brand} onChange={set('brand')} className={ic}><option value="">-- เลือก Brand --</option>{brands.map(b=><option key={b.id} value={b.name}>{b.name}</option>)}</select>
                : <input value={form.brand} onChange={set('brand')} className={ic} placeholder="Brand..."/>}
            </Field>
            <Field label="Model"><input value={form.model} onChange={set('model')} className={ic} placeholder="Model..."/></Field>
          </Grid>
        </Sec>

 


       {/* ตำแหน่ง */}
        <Sec title="COMPANY & Site">
          <Grid>
            <Field label="Company">
              <select value={form.companyId} onChange={e=>{const v=e.target.value;const name=companies.find(c=>c.id===v)?.name||'';setForm(p=>({...p,companyId:v,company:name,siteId:'',site:'',departmentId:'',room:''}))}} className={ic}>
                <option value="">-- เลือก Company --</option>
                {companies.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Field>
            <Field label="Site">
              <select value={form.siteId} onChange={e=>{const v=e.target.value;const name=allSites.find(s=>s.id===v)?.name||'';setForm(p=>({...p,siteId:v,site:name,departmentId:'',room:''}))}} className={ic} disabled={!form.companyId}>
                <option value="">-- เลือก Site --</option>
                {filteredSites.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </Field>
            <Field label="Department / แผนก">
              <select value={form.departmentId} onChange={e=>setForm(p=>({...p,departmentId:e.target.value}))} className={ic} disabled={!form.siteId}>
                <option value="">-- เลือกแผนก --</option>
                {filteredDepts.map(d=><option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </Field>
          </Grid>
        </Sec>
        <Sec title="ตำแหน่ง">
          <Grid>
            <Field label="ผู้ใช้งาน">
              <div className="relative">
                <input
                  value={userSearch || form.assignedTo}
                  onChange={e => { setUserSearch(e.target.value); if (!e.target.value) setForm(f=>({...f,assignedTo:'',employeeId:''})) }}
                  className={ic}
                  placeholder="ค้นหาชื่อ, email, รหัสพนักงาน..."
                />
                {userSearch && filteredUsers.length > 0 && (
                  <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-52 overflow-y-auto">
                    {filteredUsers.map(u => (
                      <button key={u.id} type="button"
                        onMouseDown={() => handleSelectUser(u.id)}
                        className="w-full text-left px-3 py-2.5 hover:bg-blue-50 transition-colors border-b border-gray-50 last:border-0">
                        <p className="text-sm font-medium text-gray-900">{u.displayName || u.username}</p>
                        <p className="text-xs text-gray-400">{[u.email, u.employeeId].filter(Boolean).join(' · ')}</p>
                      </button>
                    ))}
                  </div>
                )}
                {form.assignedTo && !userSearch && (
                  <p className="text-xs text-gray-400 mt-1">เลือกแล้ว: <span className="text-blue-600 font-medium">{form.assignedTo}</span> — พิมพ์เพื่อเปลี่ยน</p>
                )}
              </div>
            </Field>
            <Field label="รหัสพนักงาน"><input value={form.employeeId} onChange={set('employeeId')} className={ic} placeholder="IL67001"/></Field>
            <Field label="ห้อง / ที่ตั้ง">
              <select value={form.room} onChange={set('room')} className={ic} disabled={!form.siteId}>
                <option value="">-- เลือกห้อง --</option>
                {filteredRooms.map(r=><option key={r.id} value={r.name}>{r.name}</option>)}
              </select>
            </Field>
            <Field label="อาคาร (Building)"><input value={form.building} onChange={set('building')} className={ic} placeholder="อาคาร A..."/></Field>
            <Field label="ชั้น (Floor)"><input value={form.floor} onChange={set('floor')} className={ic} placeholder="ชั้น 3..."/></Field>
          </Grid>
        </Sec>

        {/* Vendor / สัญญา */}
        <Sec title="Vendor & สัญญาบริการ">
          <Grid>
            <Field label="Vendor / ผู้ให้บริการ">
              <select value={form.vendorId} onChange={set('vendorId')} className={ic}>
                <option value="">-- เลือก Vendor --</option>
                {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </Field>
            <Field label="สัญญาบริการ"><input value={form.serviceContract} onChange={set('serviceContract')} className={ic} placeholder="เลขที่สัญญา..."/></Field>
          </Grid>
        </Sec>

        {/* ใบส่งมอบ & MA */}
        <Sec title="MA">
          <Grid>
            <Field label="วันที่ MA ล่าสุด"><DateInput value={form.maDate} onChange={v => setForm(f=>({...f,maDate:v}))} className={ic}/></Field>
            <Field label="กำหนด MA ถัดไป"><DateInput value={form.nextMaDate} onChange={v => setForm(f=>({...f,nextMaDate:v}))} className={ic}/></Field>
          </Grid>
        </Sec>

        {/* เครือข่าย */}
        <Sec title="ข้อมูลเครือข่าย">
          <Grid>
            <Field label="IP Address"><input value={form.ipAddress} onChange={set('ipAddress')} className={ic} placeholder="192.168.1.x"/></Field>
            <Field label="Ethernet MAC"><input value={form.macEthernet} onChange={set('macEthernet')} className={ic} placeholder="XX:XX:XX:XX:XX:XX"/></Field>
            <Field label="WiFi MAC"><input value={form.macWifi} onChange={set('macWifi')} className={ic} placeholder="XX:XX:XX:XX:XX:XX"/></Field>
            <Field label="Bluetooth MAC"><input value={form.macBluetooth} onChange={set('macBluetooth')} className={ic} placeholder="XX:XX:XX:XX:XX:XX"/></Field>
          </Grid>
        </Sec>

        {/* การเงิน */}
        <Sec title="การเงิน & สัญญา">
          <Grid>
            <Field label="วันที่ซื้อ / เริ่มเช่า"><DateInput value={form.purchaseDate} onChange={v => setForm(f=>({...f,purchaseDate:v}))} className={ic}/></Field>
            <Field label="วันหมดประกัน"><DateInput value={form.warrantyExpiry} onChange={v => setForm(f=>({...f,warrantyExpiry:v}))} className={ic}/></Field>
            <Field label="หมดสัญญาเช่า"><DateInput value={form.contractEnd} onChange={v => setForm(f=>({...f,contractEnd:v}))} className={ic}/></Field>
            <Field label="ราคาซื้อ (฿)"><input type="number" value={form.purchasePrice} onChange={set('purchasePrice')} className={ic} placeholder="0.00"/></Field>
            <Field label="มูลค่าปัจจุบัน (฿)"><input type="number" value={form.currentValue} onChange={set('currentValue')} className={ic} placeholder="0.00"/></Field>
            <Field label="Net Book Value (฿)"><input type="number" value={form.netBook} onChange={set('netBook')} className={ic} placeholder="0.00"/></Field>
            <Field label="ค่าเสื่อมราคา (฿)"><input type="number" value={form.depreciation} onChange={set('depreciation')} className={ic} placeholder="0.00"/></Field>
            <Field label="ราคาเช่า (฿/เดือน)"><input type="number" value={form.rentPrice} onChange={set('rentPrice')} className={ic} placeholder="0.00"/></Field>
          </Grid>
        </Sec>

        {/* Computer Specs — แสดงเมื่อเป็น LAPTOP / OTHER */}
        {(form.type === 'LAPTOP' || form.type === 'OTHER' || form.type === 'MONITOR') && (
          <Sec title="ข้อมูล Hardware">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="CPU"><input value={form.cpu} onChange={set('cpu')} className={ic} placeholder="เช่น Core i5-13420H, 12MB Cache"/></Field>
              <Field label="VGA / GPU"><input value={form.vga} onChange={set('vga')} className={ic} placeholder="เช่น Intel UHD Graphics, 16GB"/></Field>
              <Field label="RAM"><input value={form.ram} onChange={set('ram')} className={ic} placeholder="เช่น 16GB DDR5"/></Field>
              <Field label="HDD / SSD"><input value={form.hdd} onChange={set('hdd')} className={ic} placeholder="เช่น SSD 512GB NVMe"/></Field>
              <Field label="Monitor"><input value={form.monitorSpec} onChange={set('monitorSpec')} className={ic} placeholder="รุ่น/ขนาด Monitor"/></Field>
            </div>
          </Sec>
        )}

        <Sec title="License & หมายเหตุ">
          <Field label="License Info"><input value={form.licenseInfo} onChange={set('licenseInfo')} className={ic} placeholder="License key..."/></Field>
          <Field label="หมายเหตุ"><textarea value={form.notes} onChange={set('notes')} rows={3} className={ic} placeholder="บันทึกเพิ่มเติม..."/></Field>
        </Sec>

        {error && <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</p>}
      </form>

      </div>{/* /inner padding */}
      </div>{/* /scroll wrapper */}

      {deliveryPrompt && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle size={22} className="text-amber-500"/>
            </div>
            <h3 className="font-semibold text-gray-900 text-center mb-2">ผู้ใช้งานเปลี่ยนแล้ว</h3>
            <p className="text-sm text-gray-500 text-center mb-1">
              {deliveryPrompt.oldUser
                ? <><span className="line-through text-gray-400">{deliveryPrompt.oldUser}</span> → <span className="font-medium text-gray-800">{deliveryPrompt.newUser}</span></>
                : <>กำหนดให้ <span className="font-medium text-gray-800">{deliveryPrompt.newUser}</span></>}
            </p>
            <p className="text-sm text-gray-500 text-center mb-6">กรุณาสร้างใบส่งมอบใหม่สำหรับผู้ใช้งานคนนี้</p>
            <div className="flex gap-2">
              <button
                onClick={() => navigate(`/assets/${deliveryPrompt.assetId}`)}
                className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50">
                ข้ามไปก่อน
              </button>
              <button
                onClick={() => navigate(`/deliveries/new?assetId=${deliveryPrompt.assetId}&assignedTo=${encodeURIComponent(deliveryPrompt.newUser)}&employeeId=${encodeURIComponent(deliveryPrompt.employeeId)}`)}
                className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 flex items-center justify-center gap-2">
                <ClipboardList size={15}/> สร้างใบส่งมอบ
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
